/** @jsxImportSource @emotion/react */

import { vars } from "@imwebme/clay-token";
import type { PropsWithChildren } from "react";
import { useMemo } from "react";
import { Flex, TextButton, Typography } from "../..";
import { iconMap } from "./PageBanner.constants";
import { pageBannerCSS, variantStyle } from "./PageBanner.css";
import type { PageBannerProps } from "./PageBanner.types";

export const PageBanner = ({
  variant = "default",
  action,
  text,
  className,
  sx,
  icon,
  buttonText,
  primaryButtonText,
  secondaryButtonText,
  children,
  onClick,
  onClickPrimary,
  as: Element = "div",
  native,
  onClickSecondary,
}: PropsWithChildren<PageBannerProps>) => {
  const buttonArea = useMemo(() => {
    const textButtonVariant =
      variant === "information" || variant === "default" ? "primary" : variant;

    switch (action) {
      case "dismissible":
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onClick={onClick}
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke={vars.semantic.color.icon}
              strokeWidth="1.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        );

      case "primary":
        return (
          buttonText && (
            <TextButton variant={textButtonVariant} text={buttonText} onClick={onClick} />
          )
        );

      case "primary+secondary":
        return (
          <Flex gap={vars.spacing[2]}>
            <TextButton variant="secondary" text={secondaryButtonText} onClick={onClickSecondary} />
            <TextButton
              variant={textButtonVariant}
              text={primaryButtonText}
              onClick={onClickPrimary}
            />
          </Flex>
        );

      default:
        return children;
    }
  }, [
    action,
    buttonText,
    children,
    onClick,
    onClickPrimary,
    onClickSecondary,
    primaryButtonText,
    secondaryButtonText,
    variant,
  ]);

  return (
    <Element className={className} css={[pageBannerCSS, variantStyle[variant], sx]} {...native}>
      <Flex alignItems="center" gap={vars.spacing[4]} padding={`${vars.spacing["05"]} 0`}>
        {icon && <Flex>{iconMap[variant]}</Flex>}
        <Typography>{text}</Typography>
      </Flex>

      {buttonArea}
    </Element>
  );
};
